import { DatePicker, DatePickerProps } from 'antd'
import cx from 'classnames'
import dayjs from 'dayjs'
import { FC, memo, useState } from 'react'
import { WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form'
import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg'
import { formFieldID } from '../../utils/helpers'

type IDatePickerField = DatePickerProps & {
	label?: string
	input: Partial<WrappedFieldInputProps>
	meta: Partial<WrappedFieldMetaProps>
	inputClassName?: string
	startWithYear?: boolean
}

const DatePickerField: FC<IDatePickerField> = ({ label, placeholder, disabled, className, style, input, meta, inputClassName, startWithYear, ...props }) => {
	const { name = 'field', value = undefined, onChange = undefined } = input
	const { form = 'form', error = undefined, touched = false } = meta

	const id = formFieldID(form, name)

	const [open, setOpen] = useState(false)
	const [mode, setMode] = useState<'year' | 'month' | 'date'>('year')

	const handlePanelChange = (newValue: any, newMode: 'year' | 'month' | 'date') => {
		if (onChange) {
			// Convert to dayjs and format as date string to avoid timezone issues
			onChange(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null)
		}
		setMode(newMode)
	}

	const handleOpenChange = (newOpen: boolean) => {
		if (newOpen) {
			setOpen(true)
		} else {
			setOpen(false)
			setMode('year')
		}
	}

	const handleChange = (newValue: any) => {
		if (onChange) {
			// Convert to dayjs and format as date string to avoid timezone issues
			onChange(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null)
		}
		setOpen(false)
		setMode('year')
	}

	const handleRegularChange = (newValue: any) => {
		if (onChange) {
			// Convert to dayjs and format as date string to avoid timezone issues
			onChange(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null)
		}
	}

	const additionalProps = startWithYear
		? {
				open,
				mode,
				onOpenChange: handleOpenChange,
				onPanelChange: handlePanelChange as any,
				onChange: handleChange as any
			}
		: {
				onChange: handleRegularChange
			}

	return (
		<div className={cx('form-field', className)} style={style}>
			{label && (
				<label htmlFor={id} className={'form-field-label'}>
					{label}
				</label>
			)}
			<div className={'form-field-item'}>
				<DatePicker
					id={id}
					placeholder={placeholder}
					disabled={disabled}
					value={value ? (dayjs(value) as any) : undefined}
					className={cx(inputClassName, { error: error && touched })}
					format={'DD.MM.YYYY'}
					allowClear={false}
					{...props}
					{...additionalProps}
				/>
				<span className={'form-field-item-suffix'}>{error && touched && <ErrorIcon />}</span>
			</div>
			{error && touched && <span className={'form-field-message'}>{error}</span>}
		</div>
	)
}

export default memo(DatePickerField)
